// src/components/ImportDialog.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';

/* =========================
   Helpers
========================= */
const generatePoNumber = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `PO${yyyy}${mm}${dd}${hh}${min}${ss}`;
};

const formatCurrency = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0 ₫';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(Number(number));
};

/* =========================
   Component
========================= */
function ImportDialog({
  open,
  onClose,
  onSave,
  products = [],
  suppliers = [],
  onAddNewSupplier,
  newlyCreatedSupplier,
  initialImportOrder
}) {
  const [poNumber, setPoNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [warehouse, setWarehouse] = useState('Kho chính');
  const [note, setNote] = useState('');
  const [items, setItems] = useState([{ product: null, quantity: 1, importPrice: 0, isReturn: false }]);

  const grandTotal = useMemo(
    () =>
      items.reduce(
        (sum, item) => sum + Number(item.quantity || 0) * Number(item.importPrice || 0),
        0
      ),
    [items]
  );

  /* Khi mở dialog: tạo mới hoặc nạp dữ liệu chỉnh sửa */
  useEffect(() => {
    if (!open) return;

    if (initialImportOrder) {
      setPoNumber(initialImportOrder.poNumber || generatePoNumber());
      setInvoiceNumber(initialImportOrder.invoiceNumber || '');
      const supplierFound = suppliers.find((s) => s.id === initialImportOrder.supplierId) || null;
      setSelectedSupplier(supplierFound);
      setWarehouse(initialImportOrder.warehouse || 'Kho chính');
      setNote(initialImportOrder.note || '');

      const itemsWithProductObjects =
        Array.isArray(initialImportOrder.items)
          ? initialImportOrder.items.map((it) => ({
              product: products.find((p) => p.id === it.productId) || null,
              quantity: it.quantity ?? 1,
              importPrice: it.importPrice ?? 0,
              isReturn: !!it.isReturn
            }))
          : [];

      setItems(
        itemsWithProductObjects.length > 0
          ? itemsWithProductObjects
          : [{ product: null, quantity: 1, importPrice: 0, isReturn: false }]
      );
    } else {
      // tạo mới
      setPoNumber(generatePoNumber());
      setInvoiceNumber('');
      setSelectedSupplier(null);
      setWarehouse('Kho chính');
      setNote('');
      setItems([{ product: null, quantity: 1, importPrice: 0, isReturn: false }]);
    }
  }, [open, initialImportOrder, products, suppliers]);

  /* Nếu vừa tạo NCC mới từ dialog khác → tự chọn vào đây */
  useEffect(() => {
    if (newlyCreatedSupplier) {
      setSelectedSupplier(newlyCreatedSupplier);
    }
  }, [newlyCreatedSupplier]);

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      if (field === 'isReturn') {
        next[index][field] = value.target.checked;
      } else {
        next[index][field] = value;
      }
      if (field === 'product' && value) {
        // tự điền giá nhập mặc định theo sản phẩm (nếu có)
        next[index].importPrice = value.price ?? 0;
      }
      return next;
    });
  };

  const handleAddItem = () => {
    setItems((prev) => [...prev, { product: null, quantity: 1, importPrice: 0, isReturn: false }]);
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!selectedSupplier) {
      alert('Vui lòng chọn nhà cung cấp.');
      return;
    }
    const invalid = items.find(
      (it) => !it.product || !it.quantity || Number(it.quantity) <= 0
    );
    if (invalid) {
      alert('Vui lòng đảm bảo tất cả các dòng sản phẩm đã chọn và số lượng > 0.');
      return;
    }

    const importData = {
      supplierId: selectedSupplier.id,
      supplierName: selectedSupplier.name,
      supplierCode: selectedSupplier.code,
      poNumber,
      invoiceNumber,
      warehouse,
      note,
      totalAmount: grandTotal,
      items: items.map((it) => ({
        productId: it.product.id,
        productName: it.product.name,
        productSku: it.product.sku,
        quantity: Number(it.quantity),
        importPrice: Number(it.importPrice),
        isReturn: !!it.isReturn
      }))
    };

    onSave?.(importData);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        {initialImportOrder
          ? `Chỉnh sửa phiếu nhập ${initialImportOrder.poNumber}`
          : 'Tạo Phiếu Nhập Hàng Mới'}
      </DialogTitle>

      <DialogContent>
        {/* Khu vực thông tin chung */}
        <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
          {/* Ô TÌM KIẾM NCC – làm TO và RỘNG */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                fullWidth
                autoHighlight
                sx={{
                  flexGrow: 1,
                  minWidth: 560, // đảm bảo ô không bị hẹp
                  // tăng chiều cao và cỡ chữ
                  '& .MuiOutlinedInput-root': { height: 56 },
                  '& .MuiInputBase-input': { fontSize: 16 },
                  '& .MuiInputLabel-root': { fontSize: 16 }
                }}
                options={suppliers}
                getOptionLabel={(option) => `${option.name} — (Mã: ${option.code})`}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                value={selectedSupplier}
                onChange={(_, val) => setSelectedSupplier(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Tìm kiếm và chọn nhà cung cấp (*)"
                    variant="outlined"
                    size="medium"
                  />
                )}
              />
              <IconButton
                color="primary"
                onClick={onAddNewSupplier}
                title="Thêm nhà cung cấp mới"
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField label="Mã PO" value={poNumber} fullWidth InputProps={{ readOnly: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Số hóa đơn"
              fullWidth
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Kho nhập"
              fullWidth
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Ghi chú" fullWidth value={note} onChange={(e) => setNote(e.target.value)} />
          </Grid>
        </Grid>

        {/* Khu vực chi tiết sản phẩm */}
        <Typography variant="h6" gutterBottom>
          Chi tiết sản phẩm
        </Typography>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '30%' }}>Sản phẩm (*)</TableCell>
                <TableCell sx={{ width: '15%' }}>Số lượng (*)</TableCell>
                <TableCell sx={{ width: '15%' }}>Giá nhập</TableCell>
                <TableCell sx={{ width: '15%' }}>Loại</TableCell>
                <TableCell sx={{ width: '15%' }} align="right">
                  Thành tiền
                </TableCell>
                <TableCell sx={{ width: '10%' }} align="center">
                  Xóa
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Autocomplete
                      fullWidth
                      autoHighlight
                      sx={{
                        '& .MuiOutlinedInput-root': { height: 48 },
                        '& .MuiInputBase-input': { fontSize: 14 }
                      }}
                      options={products}
                      getOptionLabel={(option) => `${option.name} (${option.sku})`}
                      value={item.product}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onChange={(_, val) => handleItemChange(index, 'product', val)}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" size="medium" fullWidth />
                      )}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      inputProps={{ min: 0, step: 'any' }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={item.importPrice}
                      onChange={(e) => handleItemChange(index, 'importPrice', e.target.value)}
                      inputProps={{ min: 0, step: 'any' }}
                    />
                  </TableCell>

                  <TableCell>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!item.isReturn}
                          onChange={(e) => handleItemChange(index, 'isReturn', e)}
                          size="small"
                        />
                      }
                      label="Trả vỏ"
                    />
                  </TableCell>

                  <TableCell align="right">
                    {formatCurrency(Number(item.quantity || 0) * Number(item.importPrice || 0))}
                  </TableCell>

                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleRemoveItem(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TableCell colSpan={4} align="right">
                  <Typography variant="h6">Tổng Cộng:</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="h6">{formatCurrency(grandTotal)}</Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        <Button startIcon={<AddCircleOutlineIcon />} onClick={handleAddItem} sx={{ mt: 1 }}>
          Thêm dòng
        </Button>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Hủy</Button>
        <Button onClick={handleSave} variant="contained">
          Lưu Phiếu Nhập
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ImportDialog;
